import React, { useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useSatisfactionByOrderType } from '../../hooks/useSatisfactionQueries';
import { useTheme } from '../../contexts/ThemeContext';
import { ChartControls, HorizontalTableShell } from '../ui';
import useChartGuide from '../../hooks/useChartGuide';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import { CHART_GUIDES } from '../../constants/chartGuides';
import { getChartColors, getChartBackgroundColor, getChartJsOptions, formatTooltipLabel, mergeChartOptions } from '../../utils/themeHelpers';
import { TYPOGRAPHY_CONFIG } from '../../config/typography.config';
import { TimePeriod } from '../../types/api';

interface OrderTypeSatisfactionProps {
  branchId?: number;
  timeRange?: TimePeriod;
  onError?: (error: Error) => void;
  showLoading?: boolean;
  compact?: boolean;
}

const OrderTypeSatisfaction: React.FC<OrderTypeSatisfactionProps> = ({
  branchId = 0,
  timeRange,
  onError,
  showLoading = true,
  compact = false
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { currentTheme } = useTheme();
  const { openGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const { data: orderTypeData, isLoading, error } = useSatisfactionByOrderType(branchId, timeRange);
  const [viewMode, setViewMode] = useState<'chart' | 'table'>('chart');
  
  // Component metadata
  const componentId = 'order-type-satisfaction';
  const componentTitle = 'رضایت مشتریان بر اساس نوع سفارش';

  // Handle error
  React.useEffect(() => {
    if (error && onError) {
      onError(error as Error);
    }
  }, [error, onError]);

  // Use theme-aware colors
  const chartColors = getChartColors(currentTheme);

  const orderTypeColorMapping = {
    'سالن': 0,  // Dine-in
    'بسته بندی': 1,  // Takeaway
    'دلیوری': 2,  // Delivery
    'اسنپ': 6,  
    'سایت اختصاصی': 7,  
    '0': 0, // Dine-in
    '1': 1, // Takeaway
    '2': 2, // Delivery
    '6': 3, // Other 1
    '7': 4, // Other 2
  };

  // Helper function to get emoji based on satisfaction percentage
  const getSatisfactionEmoji = (percentage: number): string => {
    if (percentage >= 90) return '😍';
    if (percentage >= 80) return '😊';
    if (percentage >= 70) return '🙂';
    if (percentage >= 60) return '😐';
    if (percentage >= 50) return '😕';
    if (percentage >= 40) return '☹️';
    return '😢';
  };

  // Prepare data for order type satisfaction chart
  const prepareOrderTypeChartData = () => {
    if (!orderTypeData || !orderTypeData.length) return { labels: [], datasets: [] };

    // Get unique months and order types (ascending for chart - oldest to newest)
    const months = Array.from(new Set(orderTypeData.map(d => d.month)))
      .sort((a, b) => {
        const aItem = orderTypeData.find(d => d.month === a);
        const bItem = orderTypeData.find(d => d.month === b);
        if (aItem && bItem) {
          return aItem.yearMonth - bItem.yearMonth; // Keep ascending for chart
        }
        return 0;
      });
    
    const orderTypes = Array.from(new Set(orderTypeData.map(d => d.order_type)));
    
    // Create datasets for each order type
    const datasets = orderTypes.map(orderType => {
      const colorIndex = (orderTypeColorMapping as any)[orderType] || 0;
      const color = chartColors[colorIndex % chartColors.length];
      
      // Create a direct rgba background color with 0.2 opacity
      const rgbaColor = color.startsWith('#') ? 
        `rgba(${parseInt(color.slice(1,3),16)}, ${parseInt(color.slice(3,5),16)}, ${parseInt(color.slice(5,7),16)}, 0.2)` :
        getChartBackgroundColor(colorIndex, currentTheme, 0.2);
        
      return {
        label: orderType,
        data: months.map(month => {
          const dataPoint = orderTypeData.find(d => d.month === month && d.order_type === orderType);
          return dataPoint ? dataPoint.satisfaction_percentage : null;
        }),
        borderColor: color,
        backgroundColor: rgbaColor,
        borderWidth: 2,
        pointBackgroundColor: color,
        pointBorderColor: currentTheme === 'light' ? '#FFFFFF' : '#27272A',
        pointRadius: 6,
        pointHoverRadius: 10,  
        tension: 0.3,
        fill: false
      };
    });
    
    return { labels: months, datasets };
  };

  // Prepare order type surveys table data
  const prepareOrderTypeTableData = () => {
    if (!orderTypeData || !orderTypeData.length) return { headers: [], rows: [] };

    // Get unique months and sort by yearMonth (descending - most recent first)
    const months = Array.from(new Set(orderTypeData.map(d => d.month)))
      .sort((a, b) => {
        const aItem = orderTypeData.find(d => d.month === a);
        const bItem = orderTypeData.find(d => d.month === b);
        if (aItem && bItem) {
          return bItem.yearMonth - aItem.yearMonth; // Changed to descending order
        }
        return 0;
      });
    
    // Get all unique order types from the actual data
    const uniqueOrderTypes = Array.from(new Set(orderTypeData.map(d => d.order_type)));
    
    // Define order for known types, then add any others
    const orderTypeOrder = ['سالن', 'بسته بندی', 'دلیوری', 'اسنپ', 'سایت اختصاصی'];
    const orderedOrderTypes = [
      ...orderTypeOrder.filter(type => uniqueOrderTypes.includes(type)),
      ...uniqueOrderTypes.filter(type => !orderTypeOrder.includes(type))
    ];
    
    // Group by month to show each month as a single row with order type columns
    const monthGroups = new Map<string, number>();
    months.forEach(month => {
      const dataPoints = orderTypeData.filter(d => d.month === month);
      const monthYearMonth = dataPoints[0]?.yearMonth || 0;
      monthGroups.set(month, monthYearMonth);
    });
    
    // Create a single row for each month with all order types as columns
    const rows = Array.from(monthGroups.entries()).map(([month, yearMonth]) => {
      const rowData: Record<string, any> = { 
        month,
        yearMonth
      };
      
      let rowTotal = 0;
      let satisfiedTotal = 0;

      // Add each order type as a column
      orderedOrderTypes.forEach(orderType => {
        const dataPoint = orderTypeData.find(d => d.month === month && d.order_type === orderType);
        const surveys = dataPoint ? dataPoint.total_surveys : 0;
        rowData[orderType] = {
          count: surveys,
          satisfaction: dataPoint?.satisfaction_percentage ?? null
        };
        rowTotal += surveys;
        satisfiedTotal += dataPoint?.Satisfiedcount ?? 0;
      });

      rowData['total'] = rowTotal;
      rowData['totalSatisfaction'] = rowTotal > 0 ? (satisfiedTotal / rowTotal) * 100 : null;
      return rowData;
    });
    
    // Sort rows by yearMonth (descending - most recent first)
    rows.sort((a, b) => b.yearMonth - a.yearMonth);
    
    return { 
      headers: ['ماه', ...orderedOrderTypes, 'مجموع'], 
      rows
    };
  };

  // Calculate dynamic Y-axis range
  const calculateDynamicYRange = (data: any[]) => {
    if (!data.length) return { min: 0, max: 105 };
    
    const values = data.flatMap(dataset => 
      dataset.data.filter((value: any) => value !== null && value !== undefined)
    );
    
    if (values.length === 0) return { min: 0, max: 105 };
    
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const range = maxValue - minValue;
    
    if (range < 20 && minValue > 20) {
      const padding = Math.max(5, range * 0.2);
      return {
        min: Math.max(0, Math.floor(minValue - padding)),
        max: Math.min(100, Math.ceil(maxValue + padding))
      };
    }
    
    return { min: 0, max: 105 };
  };

  const orderTypeChartData = prepareOrderTypeChartData();
  const yAxisRange = calculateDynamicYRange(orderTypeChartData.datasets);

  const chartOptions = mergeChartOptions(getChartJsOptions(currentTheme), {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: !compact
      },
      tooltip: {
        ...getChartJsOptions(currentTheme).plugins.tooltip,
        callbacks: {
          label: (context: any) => {
            if (context.parsed.y === null) return '';
            
            const label = context.dataset.label || '';
            const percentage = context.parsed.y;
            const value = percentage.toFixed(1) + '%';
            const emoji = getSatisfactionEmoji(percentage);
            
            return formatTooltipLabel(label, value);
          }
        }
      }
    },
    scales: {
      ...getChartJsOptions(currentTheme).scales,
      y: {
        ...getChartJsOptions(currentTheme).scales?.y,
        min: yAxisRange.min,
        max: yAxisRange.max,
        title: {
          display: !compact,
          text: 'درصد رضایتمندی',
          color: currentTheme === 'light' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
          font: {
            family: 'var(--font-primary)',
            size: TYPOGRAPHY_CONFIG.components.chart.axisLabel.size,
            weight: TYPOGRAPHY_CONFIG.components.chart.axisLabel.weight,
          }
        },
        ticks: {
          ...getChartJsOptions(currentTheme).scales?.y?.ticks,
          callback: (value: any) => { return value + '%'; },
          stepSize: yAxisRange && (yAxisRange.max - yAxisRange.min) < 30 ? 5 : 10,
        }
      },
      x: {
        ...getChartJsOptions(currentTheme).scales?.x,
        title: {
          display: !compact,
          text: 'ماه',
          color: currentTheme === 'light' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
          font: {
            family: 'var(--font-primary)',
            size: TYPOGRAPHY_CONFIG.components.chart.axisLabel.size,
            weight: TYPOGRAPHY_CONFIG.components.chart.axisLabel.weight,
          }
        },
      }
    },
    elements: (getChartJsOptions(currentTheme) as any).plugins?.elements || {
      point: {
        radius: 0,
        hoverRadius: 8,
        hoverBorderWidth: 3,
        borderWidth: 2,
      },
      line: {
        tension: 0.4,
        borderCapStyle: 'round' as const,
        borderJoinStyle: 'round' as const,
      },
    }
  });

  // Handle loading state
  if (isLoading && showLoading) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <div className="animate-pulse">
          <div className="h-6 bg-bg-secondary rounded w-1/3 mb-4"></div>
          <div className="h-80 bg-bg-secondary rounded"></div>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error || !orderTypeData) {
    return null;
  }

  return (
    <>
      <div ref={chartRef} className="card-base card-padding animate-enter animate-delay-3">
        <div className="mb-6">
          {!compact && (
            <ChartControls
              componentId={componentId}
              componentTitle={componentTitle}
              componentType="mixed"
              dashboardSource="satisfaction"
              onInfo={() => openGuide(CHART_GUIDES.orderTypeSatisfaction)}
              onFavorite={() => handleFavoriteToggle({
                componentId,
                componentTitle,
                dashboardSource: 'satisfaction',
                componentType: 'mixed',
                description: 'نمودار و جدول رضایت مشتریان بر اساس نوع سفارش',
                props: { branchId, timeRange, onError, showLoading, compact },
                gridLayout: {
                  containerClass: 'card-base card-padding'
                }
              })}
              isFavorited={isFavorited(componentId)}
              className="mb-4"
              chartRef={chartRef}
            />
          )}
          <h3 className="title-card">
            {viewMode === 'chart' ? componentTitle : 'تعداد نظرسنجی‌ها به تفکیک نوع سفارش'}
          </h3>
          {!compact && (
            <div className="flex items-center justify-between">
              <div className="flex gap-2">
                <button
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    viewMode === 'chart'
                      ? 'bg-primary-600 text-text-inverse shadow-md'
                      : 'bg-bg-secondary text-text-secondary hover:bg-bg-tertiary hover:text-text-primary'
                  }`}
                  onClick={() => setViewMode('chart')}
                >
                  نمودار رضایت مشتریان
                </button>
                <button
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    viewMode === 'table'
                      ? 'bg-primary-600 text-text-inverse shadow-md'
                      : 'bg-bg-secondary text-text-secondary hover:bg-bg-tertiary hover:text-text-primary'
                  }`}
                  onClick={() => setViewMode('table')}
                >
                  جدول تعداد نظرسنجی‌ها
                </button>
              </div>
            </div>
          )}
        </div>

        {viewMode === 'chart' ? (
          <div className={compact ? "h-60" : "h-80"}>
            <Line 
              data={orderTypeChartData} 
              options={chartOptions} 
              height={compact ? 240 : 300}
            />
          </div>
        ) : (
          (() => {
            const orderTypeTable = prepareOrderTypeTableData();
            return (
              <HorizontalTableShell minWidth={Math.max(720, orderTypeTable.headers.length * 110)}>
                  <table className="w-full border-collapse" style={{ direction: 'rtl' }}>
                    <thead>
                      <tr className="border-b-2 !bg-gray-100 dark:!bg-gray-800" style={{ borderColor: 'var(--border-secondary)' }}>
                        {orderTypeTable.headers.map((header, index) => {
                          const isTotal = header === 'مجموع';
                          return (
                            <th
                              key={index}
                              className={`px-3 py-4 text-center text-sm font-bold border-l last:border-l-0 ${isTotal ? '!bg-primary-600 text-text-inverse' : 'text-text-primary'}`}
                              style={{ borderColor: 'var(--border-primary)' }}
                            >
                              {header}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {orderTypeTable.rows.map((row, rowIndex) => (
                        <tr
                          key={rowIndex}
                          className={`transition-all border-b ${rowIndex % 2 === 0 ? '!bg-white dark:!bg-gray-900' : '!bg-gray-50 dark:!bg-gray-800'} hover:!bg-blue-50 dark:hover:!bg-gray-700`}
                          style={{ borderColor: 'var(--border-primary)' }}
                        >
                          {orderTypeTable.headers.map((header, cellIndex) => {
                            const isTotal = header === 'مجموع';
                            const isMonth = header === 'ماه';

                            if (isMonth) {
                              return (
                                <td
                                  key={cellIndex}
                                  className="px-3 py-4 whitespace-nowrap text-center text-sm font-bold border-l last:border-l-0 text-text-primary bg-bg-tertiary"
                                  style={{ borderColor: 'var(--border-primary)' }}
                                >
                                  {row.month}
                                </td>
                              );
                            }

                            const cellData = isTotal
                              ? { count: row.total || 0, satisfaction: row['totalSatisfaction'] as number | null }
                              : (row[header] as { count: number; satisfaction: number | null }) || { count: 0, satisfaction: null };

                            return (
                              <td
                                key={cellIndex}
                                className={`px-3 py-4 whitespace-nowrap text-center text-sm border-l last:border-l-0 ${isTotal ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-bold' : 'text-text-secondary font-medium'}`}
                                style={{ borderColor: 'var(--border-primary)' }}
                              >
                                <div className="flex flex-col items-center gap-1">
                                  <span className="font-semibold">{cellData.count}</span>
                                  {cellData.satisfaction != null && cellData.count > 0 && (
                                    <span className={`text-xs font-normal ${isTotal ? 'text-primary-600 dark:text-primary-300' : 'text-text-tertiary'}`}>
                                      ({cellData.satisfaction.toFixed(1)}%)
                                    </span>
                                  )}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
              </HorizontalTableShell>
            );
          })()
        )}
      </div>
    </>
  );
};

export default OrderTypeSatisfaction;